/**
 * Design tokens for consistent styling across dashboard widgets
 */

// Spacing scale (8px grid system)
export const spacing = {
  xs: '0.5rem',    // 8px
  sm: '0.75rem',   // 12px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  '2xl': '3rem',   // 48px
};

// Widget padding standards
export const widgetPadding = {
  default: spacing.lg,  // 24px - main widget padding
  compact: spacing.md,  // 16px - for smaller widgets
  section: spacing.sm,  // 12px - for sections within widgets
};

// Brand colors
export const brand = {
  primary: '#7d6633',        // Main brand color
  primaryLight: '#a0824a',   // Lighter variant
  primaryDark: '#5a4a26',    // Darker variant
  accent: '#d4b47a',         // Complementary accent
};

// Consistent color palette for charts and status indicators
export const colors = {
  // Status colors (consistent with BookingsStatusWidget)
  status: {
    completed: '#10B981',    // Green
    confirmed: brand.primary, // Use brand color for confirmed
    pending: '#F59E0B',      // Amber
    cancelled: '#EF4444',    // Red
    canceled: '#EF4444',     // Red (alternative spelling)
    no_show: '#6B7280',      // Gray
  },
  
  // Chart colors for consistent theming with brand color integration
  chart: {
    primary: brand.primary,   // Brand color as primary
    secondary: '#10B981',     // Green
    tertiary: '#F59E0B',      // Amber
    quaternary: brand.primaryLight, // Brand color variant
    quinary: '#EC4899',       // Pink
    senary: '#6366F1',        // Indigo
  },
  
  // Dark mode variants with brand color integration
  chartDark: {
    primary: brand.accent,    // Brand accent for dark mode
    secondary: '#34D399',     // Light green
    tertiary: '#FBBF24',      // Light amber
    quaternary: brand.primaryLight, // Brand color variant
    quinary: '#F472B6',       // Light pink
    senary: '#818CF8',        // Light indigo
  },
  
  // Barber ranking colors with brand integration
  ranking: {
    gold: '#F59E0B',         // Gold for #1
    silver: '#6B7280',       // Silver for #2
    bronze: '#EA580C',       // Bronze for #3
    default: brand.primary,  // Use brand color for others
    defaultDark: brand.accent, // Use brand accent for others (dark mode)
  },
  
  rankingDark: {
    gold: '#FBBF24',
    silver: '#9CA3AF',
    bronze: '#FB923C',
    default: brand.accent,
    defaultDark: brand.primaryLight,
  }
};

// Glass Morphism widget styling with enhanced transparency and blur
export const widgetStyles = {
  // Glass morphism base - highly transparent with strong backdrop blur
  base: 'backdrop-blur-2xl bg-white/20 dark:bg-gray-800/20 shadow-2xl shadow-black/10 dark:shadow-black/30 border border-white/20 dark:border-white/10',
  
  // Enhanced 3D hover effect with glass morphism
  hover3D: 'hover:backdrop-blur-3xl hover:bg-white/30 dark:hover:bg-gray-800/30 hover:shadow-3xl hover:shadow-black/20 dark:hover:shadow-black/40 hover:-translate-y-2 hover:border-white/30 dark:hover:border-white/20 transition-all duration-300 ease-out',
  
  // Glass morphism brand accent with subtle glow
  brandAccent: `hover:border-[${brand.primary}]/40 dark:hover:border-[${brand.accent}]/40 hover:shadow-[0_0_40px_rgba(125,102,51,0.25)] dark:hover:shadow-[0_0_40px_rgba(212,180,122,0.25)]`,
  
  // Glass-like loading shimmer effect
  shimmer: 'animate-pulse bg-gradient-to-r from-white/10 via-white/20 to-white/10 dark:from-white/5 dark:via-white/15 dark:to-white/5 bg-[length:200%_100%]',
};

// Glass morphism animation settings with enhanced floating effect
export const animations = {
  hover: {
    scale: 1.03,
    y: -8, // More dramatic lift for glass floating effect
    rotateY: 2, // Subtle 3D tilt
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 20,
      duration: 0.3  // Smoother, more elegant transitions
    }
  },
  tap: {
    scale: 0.97,
    y: -2, // Keep some lift even when pressed
    rotateY: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 25,
      duration: 0.15
    }
  },
  initial: {
    scale: 1,
    y: 0,
    rotateY: 0
  },
  
  // Loading animations
  shimmer: {
    backgroundPosition: ['0% 0%', '200% 0%'],
    transition: {
      duration: 2,
      ease: 'linear',
      repeat: Infinity,
    },
  },
};

// Typography scale with brand color integration
export const typography = {
  title: 'text-sm font-medium',
  value: 'text-2xl font-bold',
  valueBrand: `text-2xl font-bold text-[${brand.primary}] dark:text-[${brand.accent}]`, // Brand colored values
  subtitle: 'text-xs',
  label: 'text-sm',
  caption: 'text-xs',
};
